import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models
// ============================================================

export interface F9211 {
  f9211_01: string;  // 請假單號
  f9211_02: string;  // 員工代號
  f9211_03: string;  // 年月 (YYYYMM)
  f9211_11: string;  // 假別
  f9211_12: number;  // 本次請假數
  f9211_13: number;  // 計算時數
  f9211_14: string;  // 假別類型
  f9211_91: string;  // 建立人員
  f9211_92: string | null;
  f9211_93: string;
}

export interface F921 {
  f921_01: string;        // 請假單號 (PK)
  f921_02: string;        // 員工代號
  f921_10: string;        // 代理人代碼
  f921_11: string | null; // 申請日期
  f921_12: string;        // 假別代碼
  f921_13: string | null; // 開始日
  f921_14: string;        // 開始時間
  f921_15: string | null; // 結束日
  f921_16: string;        // 結束時間
  f921_17: number;        // 時數
  f921_18: string;        // 單位
  f921_19: number;        // 總請假數
  f921_21: string;        // 事由
  f921_22: string;        // 初核人員
  f921_23: string | null; // 初核日
  f921_25: string;        // 複核人員
  f921_26: string | null; // 複核日
  f921_27: string;        // 須複核原因
  f921_29: string;        // 核准層級
  f921_30: string;        // 審核狀態
  f921_31: string;        // 部門代號
  f921_72: string;        // 審核人員
  f921_73: string | null; // 審核核准日
  f921_91: string;        // 建立人員
  f921_92: string | null; // 建立日期
  f921_93: string;        // 修改人員
  details: F9211[];
}

export interface F9211Req {
  f9211_03: string;
  f9211_11: string;
  f9211_12: number;
  f9211_13: number;
  f9211_14: string;
}

export interface CreateRequest {
  f921_02: string;
  f921_10: string;
  f921_11: string;
  f921_12: string;
  f921_13: string;
  f921_14: string;
  f921_15: string;
  f921_16: string;
  f921_18: string;
  f921_19: number;
  f921_21: string;
  f921_27: string;
  f921_29: string;
  f921_31: string;
  details: F9211Req[];
}

export interface UpdateRequest {
  f921_10: string;
  f921_12: string;
  f921_13: string;
  f921_14: string;
  f921_15: string;
  f921_16: string;
  f921_18: string;
  f921_19: number;
  f921_21: string;
  f921_27: string;
  f921_29: string;
  details: F9211Req[];
}

export interface ListResponse {
  data: F921[];
  total: number;
  page: number;
  size: number;
}

export interface ValidationError {
  field: string;
  message: string;
}

export interface ApprovalResponse {
  id: string;
  action: string;
  old_status: string;
  new_status: string;
  message: string;
}

export interface ApprovalHistory {
  id: number;
  document_no: string;
  action: string;
  actor_id: string;
  actor_name: string;
  comment: string;
  old_status: string;
  new_status: string;
  created_at: string;
}

// ============================================================
// API methods
// ============================================================

export const pw9221f1Api = {
  async list(page = 1, size = 20, keyword?: string): Promise<ListResponse> {
    const params: Record<string, string | number> = { page, size };
    if (keyword) params.keyword = keyword;
    const res = await apiClient.get('/api/pw9221f1', { params });
    return res.data;
  },

  async get(id: string): Promise<F921> {
    const res = await apiClient.get(`/api/pw9221f1/${encodeURIComponent(id)}`);
    return res.data;
  },

  async create(data: CreateRequest): Promise<F921> {
    const res = await apiClient.post('/api/pw9221f1', data);
    return res.data;
  },

  async update(id: string, data: UpdateRequest): Promise<F921> {
    const res = await apiClient.put(`/api/pw9221f1/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/pw9221f1/${encodeURIComponent(id)}`);
  },

  // Approval workflow
  async submitForApproval(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/pw9221f1/${encodeURIComponent(id)}/submit`, { comment: comment || '' });
    return res.data;
  },

  async approve(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/pw9221f1/${encodeURIComponent(id)}/approve`, { comment: comment || '' });
    return res.data;
  },

  async reject(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/pw9221f1/${encodeURIComponent(id)}/reject`, { comment: comment || '' });
    return res.data;
  },

  async writeoff(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/pw9221f1/${encodeURIComponent(id)}/writeoff`, { comment: comment || '' });
    return res.data;
  },

  async getApprovalHistory(id: string): Promise<ApprovalHistory[]> {
    const res = await apiClient.get(`/api/pw9221f1/${encodeURIComponent(id)}/approval-history`);
    return res.data.data || [];
  },
};
